import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderKanban, PlusCircle, Trash2 } from 'lucide-react';
import { assetGroupsApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input, Label } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Badge } from '../components/ui/badge';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import { can } from '../lib/rbac';
import { useAuth } from '../lib/auth-context';
import type { AssetGroupZone } from '../lib/types';

const ZONE_LABEL: Record<AssetGroupZone, string> = {
  external: 'External',
  internal: 'Internal (broker-only)',
  app_api: 'Web app / API',
};

function CreateAssetGroupForm({ onDone }: { onDone: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [zone, setZone] = useState<AssetGroupZone>('external');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => assetGroupsApi.create({ name, zone }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-groups'] });
      toast({ title: 'Asset group created', variant: 'success' });
      onDone();
    },
    onError: (err) => setError(friendlyErrorMessage(err)),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-1">
        <Label htmlFor="group-name">Name</Label>
        <Input id="group-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Production web apps" required />
      </div>
      <div className="space-y-1">
        <Label htmlFor="group-zone">
          Zone <span className="font-normal text-ink-muted">— where do these assets live?</span>
        </Label>
        <Select value={zone} onValueChange={(v) => setZone(v as AssetGroupZone)}>
          <SelectTrigger id="group-zone">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.entries(ZONE_LABEL) as Array<[AssetGroupZone, string]>).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <Button type="submit" className="w-full" disabled={mutation.isPending || !name.trim()}>
        Create asset group
      </Button>
    </form>
  );
}

export function AssetGroups() {
  const { principal } = useAuth();
  const canWrite = principal?.role === undefined || can(principal.role, 'assetGroup:write');
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const query = useQuery({ queryKey: ['asset-groups'], queryFn: () => assetGroupsApi.list() });

  const removeMutation = useMutation({
    mutationFn: (id: string) => assetGroupsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-groups'] });
      toast({ title: 'Asset group removed', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not remove asset group', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const groups = query.data?.assetGroups ?? [];

  return (
    <div>
      <PageHeader
        title="Asset Groups"
        description="Organize your targets by zone — external, internal, or web app/API."
        action={
          canWrite ? (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button>
                  <PlusCircle size={16} /> New asset group
                </Button>
              </DialogTrigger>
              <DialogContent title="New asset group" description="Group related targets together, e.g. by environment or zone.">
                <CreateAssetGroupForm onDone={() => setCreateOpen(false)} />
              </DialogContent>
            </Dialog>
          ) : undefined
        }
      />

      {groups.length === 0 ? (
        <EmptyState
          icon={<FolderKanban size={32} />}
          title="No asset groups yet"
          description="Asset groups organize the targets you're authorized to test. Create one to get started."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((group) => (
            <Card key={group.id}>
              <CardHeader className="flex-row items-center justify-between space-y-0">
                <CardTitle>{group.name}</CardTitle>
                {canWrite ? (
                  <ConfirmDialog
                    trigger={
                      <Button variant="ghost" size="icon" aria-label="Remove asset group">
                        <Trash2 size={16} className="text-danger" />
                      </Button>
                    }
                    title="Remove this asset group?"
                    description={`This removes "${group.name}" from your workspace. Targets already added to it will need to be re-added to another group.`}
                    confirmLabel="Remove"
                    variant="danger"
                    onConfirm={() => removeMutation.mutateAsync(group.id)}
                  />
                ) : null}
              </CardHeader>
              <CardContent>
                <Badge variant="brand">{ZONE_LABEL[group.zone]}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
